'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

type AuthMode = 'login' | 'signup';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  brokerageName: string;
  password: string;
  plan: string;
  agreeTerms: boolean;
}

interface ForgotPasswordFormData {
  email: string;
}

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$49',
    period: '/mo',
    description: 'Single agent essentials',
    features: ['AI Dashboard', 'Contact Management', '50 AI messages/mo'],
    badge: null,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$99',
    period: '/mo',
    description: 'Full AI-powered toolkit',
    features: ['Everything in Starter', 'Unlimited AI messages', 'Social Media Generator', 'Content Calendar'],
    badge: 'Most Popular',
  },
  {
    id: 'team',
    name: 'Team',
    price: '$199',
    period: '/mo',
    description: 'Up to 5 agents',
    features: ['Everything in Professional', 'Team dashboard', 'Shared templates', 'Team leader view'],
    badge: null,
  },
];

const FEATURES = [
  { icon: 'CpuChipIcon', title: 'AI Assistant', desc: 'Intelligent AI trained on your business' },
  { icon: 'ChartBarIcon', title: 'Lead Management', desc: 'Track and nurture every lead' },
  { icon: 'UsersIcon', title: 'CRM', desc: 'Full customer relationship management' },
  { icon: 'FolderOpenIcon', title: 'Contact Organization', desc: 'Smart contact categorization' },
  { icon: 'HomeIcon', title: 'Property Tools', desc: 'Listing and property management' },
  { icon: 'MegaphoneIcon', title: 'Marketing Assistant', desc: 'AI-powered marketing creation' },
  { icon: 'EnvelopeIcon', title: 'Email Writing', desc: 'Professional emails in seconds' },
  { icon: 'DocumentTextIcon', title: 'Document Creation', desc: 'Contracts, reports, and more' },
  { icon: 'ListBulletIcon', title: 'Listing Descriptions', desc: 'Compelling property copy' },
  { icon: 'BoltIcon', title: 'Business Automation', desc: 'Automate repetitive workflows' },
  { icon: 'ClipboardDocumentCheckIcon', title: 'Task Management', desc: 'Stay on top of your to-do list' },
  { icon: 'PresentationChartBarIcon', title: 'Productivity Dashboard', desc: 'Track your business metrics' },
];

const REVIEWS = [
  {
    initials: 'MK',
    name: 'Michelle Kim',
    role: 'Keller Williams, Scottsdale AZ',
    text: '"AgentOS cut my admin time in half. The AI drafts my follow-up emails and social posts — I just review and send."',
    stars: 5,
  },
  {
    initials: 'JR',
    name: 'James Rodriguez',
    role: 'RE/MAX, Miami FL',
    text: '"The lead management and AI assistant together are a game changer. I close more deals with less effort."',
    stars: 5,
  },
  {
    initials: 'AL',
    name: 'Amanda Lee',
    role: 'Coldwell Banker, Austin TX',
    text: '"Finally an all-in-one platform that actually works. My listings get more engagement and my clients love the fast responses."',
    stars: 5,
  },
];

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const router = useRouter();
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { firstName: '', lastName: '', email: '', brokerageName: '', password: '', plan: 'professional', agreeTerms: false },
  });

  const forgotPasswordForm = useForm<ForgotPasswordFormData>({
    defaultValues: { email: '' },
  });

  const handleLogin = async (data: LoginFormData) => {
    setLoginLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back!');
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        typeof navigator !== 'undefined' ? navigator.userAgent : ''
      );
      router.push(isMobile ? '/mobile' : '/command-center');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignup = async (data: SignupFormData) => {
    setSignupLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`.trim(),
        brokerageName: data.brokerageName,
        plan: selectedPlan,
      });
      toast.success("Account created! Let's set up your AI profile.");
      router.push('/agent-onboarding');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Could not create account. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      toast.error(error?.message || 'Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (data: ForgotPasswordFormData) => {
    setForgotPasswordLoading(true);
    try {
      await resetPassword(data.email);
      setForgotPasswordSent(true);
    } catch (error: any) {
      toast.error(error?.message || 'Could not send reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordSent(false);
    forgotPasswordForm.reset();
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* ── LEFT PANEL ── dark blue brand side */}
      <div
        className="hidden lg:flex lg:w-[58%] flex-col overflow-y-auto"
        style={{ backgroundColor: '#0B1120' }}
      >
        {/* Top nav bar */}
        <div className="flex items-center justify-between px-8 py-5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Image
              src="/assets/images/AgentOS_AI_logo-1783892781172.png"
              alt="AgentOS AI logo"
              width={36}
              height={36}
              className="rounded-lg object-contain"
            />
            <span className="text-white font-700 text-lg tracking-tight">AgentOS AI</span>
          </div>
          <button
            onClick={() => setMode('signup')}
            className="px-5 py-2 rounded-full text-sm font-600 transition-all"
            style={{ backgroundColor: '#00D4FF', color: '#0B1120' }}
          >
            Try AgentOS AI
          </button>
        </div>

        {/* Hero headline */}
        <div className="px-8 pt-4 pb-6 flex-shrink-0">
          <p className="text-xs font-600 tracking-widest uppercase mb-3" style={{ color: '#00D4FF' }}>
            AI-Powered Real Estate Platform
          </p>
          <h1 className="text-white font-800 leading-tight mb-3" style={{ fontSize: 'clamp(1.6rem, 2.4vw, 2.4rem)' }}>
            Everything to run your{' '}
            <span style={{ color: '#00D4FF' }}>real estate business</span>
          </h1>
          <p className="text-white/70 text-sm leading-relaxed max-w-xl">
            Your entire real estate business in one place. AI trained on your business. Contacts, listings, communications, and marketing — all connected and intelligent.
          </p>
        </div>

        {/* Feature grid — 4 columns × 3 rows */}
        <div className="px-8 pb-6 flex-shrink-0">
          <div className="grid grid-cols-4 gap-3">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-xl p-4 flex flex-col gap-3 transition-all hover:brightness-110"
                style={{ backgroundColor: '#131C2E', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(0,212,255,0.12)' }}
                >
                  <Icon name={f.icon as any} size={18} style={{ color: '#00D4FF' }} />
                </div>
                <div>
                  <p className="text-white text-xs font-700 leading-tight mb-0.5">{f.title}</p>
                  <p className="text-white/50 text-2xs leading-tight">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div className="px-8 pb-6 flex-shrink-0">
          <div className="flex gap-8">
            {[
              { value: '2,400+', label: 'Active Agents' },
              { value: '$1.2B+', label: 'In Transactions' },
              { value: '94%', label: 'Time Saved' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-800 text-xl tabular-nums" style={{ color: '#00D4FF' }}>{stat.value}</p>
                <p className="text-white/60 text-xs font-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Reviews section */}
        <div
          className="px-8 py-6 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
        >
          <p className="text-white/50 text-xs font-600 uppercase tracking-widest mb-4">What agents are saying</p>
          <div className="grid grid-cols-3 gap-3">
            {REVIEWS.map((r) => (
              <div
                key={r.name}
                className="rounded-xl p-4"
                style={{ backgroundColor: '#131C2E', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="flex gap-0.5 mb-2">
                  {Array.from({ length: r.stars }).map((_, i) => (
                    <svg key={i} width="11" height="11" viewBox="0 0 20 20" fill="#00D4FF">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-white/80 text-2xs leading-relaxed italic mb-3">{r.text}</p>
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-2xs font-700 flex-shrink-0"
                    style={{ backgroundColor: 'rgba(0,212,255,0.2)' }}
                  >
                    {r.initials}
                  </div>
                  <div>
                    <p className="text-white text-2xs font-600">{r.name}</p>
                    <p className="text-white/40 text-2xs">{r.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── white login/signup side */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-white min-h-screen lg:min-h-0">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Image
              src="/assets/images/AgentOS_AI_logo-1783892781172.png"
              alt="AgentOS AI logo"
              width={32}
              height={32}
              className="rounded-lg object-contain"
            />
            <span className="font-800 text-lg" style={{ color: '#0B1120' }}>AgentOS AI</span>
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-xl p-1 mb-8" style={{ backgroundColor: '#F1F5F9' }}>
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-sm font-600 rounded-lg transition-all ${
                mode === 'login' ?'bg-white shadow text-gray-900' :'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2 text-sm font-600 rounded-lg transition-all ${
                mode === 'signup' ?'bg-white shadow text-gray-900' :'text-gray-500 hover:text-gray-700'
              }`}
            >
              Create Account
            </button>
          </div>

          {mode === 'login' ? (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-700 mb-1" style={{ color: '#0B1120' }}>Welcome back</h2>
                <p className="text-sm text-gray-500">Sign in to your AgentOS AI workspace</p>
              </div>

              {/* Google SSO */}
              <button
                onClick={handleGoogleAuth}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-200 rounded-xl text-sm font-500 text-gray-700 hover:bg-gray-50 transition-colors mb-5 disabled:opacity-70"
              >
                {googleLoading ? (
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                )}
                {googleLoading ? 'Redirecting to Google…' : 'Continue with Google'}
              </button>

              <div className="flex items-center gap-3 mb-5">
                <hr className="flex-1 border-gray-200" />
                <span className="text-xs text-gray-400 font-500">or</span>
                <hr className="flex-1 border-gray-200" />
              </div>

              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <div>
                  <label className="block text-sm font-500 text-gray-700 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    {...loginForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                    })}
                    placeholder="sarah@realty.com"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all placeholder-gray-400 text-gray-900"
                    style={{ '--tw-ring-color': '#00D4FF' } as React.CSSProperties}
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-500 text-gray-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...loginForm.register('password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Minimum 8 characters' },
                      })}
                      placeholder="••••••••"
                      className="w-full px-3 py-2.5 pr-10 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all placeholder-gray-400 text-gray-900"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...loginForm.register('rememberMe')}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-500">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm font-500 hover:underline"
                    style={{ color: '#00D4FF' }}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-600 rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-70"
                  style={{ backgroundColor: '#00D4FF', color: '#0B1120' }}
                >
                  {loginLoading ? (
                    <>
                      <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    'Sign In to AgentOS'
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-700 mb-1" style={{ color: '#0B1120' }}>Start your free trial</h2>
                <p className="text-sm text-gray-500">14 days free, no credit card required</p>
              </div>

              {/* Google SSO */}
              <button
                onClick={handleGoogleAuth}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-200 rounded-xl text-sm font-500 text-gray-700 hover:bg-gray-50 transition-colors mb-5 disabled:opacity-70"
              >
                {googleLoading ? (
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                )}
                {googleLoading ? 'Redirecting to Google…' : 'Sign up with Google'}
              </button>

              <div className="flex items-center gap-3 mb-5">
                <hr className="flex-1 border-gray-200" />
                <span className="text-xs text-gray-400 font-500">or</span>
                <hr className="flex-1 border-gray-200" />
              </div>

              <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-500 text-gray-700 mb-1.5">First Name</label>
                    <input
                      type="text"
                      {...signupForm.register('firstName', { required: 'Required' })}
                      placeholder="Sarah"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all placeholder-gray-400 text-gray-900"
                    />
                    {signupForm.formState.errors.firstName && (
                      <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-500 text-gray-700 mb-1.5">Last Name</label>
                    <input
                      type="text"
                      {...signupForm.register('lastName', { required: 'Required' })}
                      placeholder="Reynolds"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all placeholder-gray-400 text-gray-900"
                    />
                    {signupForm.formState.errors.lastName && (
                      <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-500 text-gray-700 mb-1.5">Work Email</label>
                  <input
                    type="email"
                    {...signupForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                    })}
                    placeholder="sarah@realty.com"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all placeholder-gray-400 text-gray-900"
                  />
                  {signupForm.formState.errors.email && (
                    <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-500 text-gray-700 mb-1.5">Brokerage Name</label>
                  <input
                    type="text"
                    {...signupForm.register('brokerageName', { required: 'Brokerage is required' })}
                    placeholder="Keller Williams Realty"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all placeholder-gray-400 text-gray-900"
                  />
                  {signupForm.formState.errors.brokerageName && (
                    <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.brokerageName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-500 text-gray-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...signupForm.register('password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Minimum 8 characters' },
                      })}
                      placeholder="Min. 8 characters"
                      className="w-full px-3 py-2.5 pr-10 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all placeholder-gray-400 text-gray-900"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {signupForm.formState.errors.password && (
                    <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.password.message}</p>
                  )}
                </div>

                {/* Plan selector */}
                <div>
                  <label className="block text-sm font-500 text-gray-700 mb-2">Choose Your Plan</label>
                  <div className="grid grid-cols-3 gap-2">
                    {PLANS.map((plan) => (
                      <button
                        key={`plan-${plan.id}`}
                        type="button"
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`relative p-3 rounded-xl border-2 text-left transition-all ${
                          selectedPlan === plan.id
                            ? 'border-cyan-400 bg-cyan-50' :'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {plan.badge && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-white text-2xs font-600 px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: '#00D4FF', color: '#0B1120' }}>
                            {plan.badge}
                          </span>
                        )}
                        <p className="text-xs font-700 text-gray-800">{plan.name}</p>
                        <p className="text-sm font-700 tabular-nums" style={{ color: '#00D4FF' }}>{plan.price}<span className="text-2xs font-400 text-gray-400">{plan.period}</span></p>
                        <p className="text-2xs text-gray-400 mt-0.5 leading-tight">{plan.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...signupForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-500 leading-relaxed">
                      I agree to the{' '}
                      <button type="button" className="hover:underline font-500" style={{ color: '#00D4FF' }}>Terms of Service</button>
                      {' '}and{' '}
                      <button type="button" className="hover:underline font-500" style={{ color: '#00D4FF' }}>Privacy Policy</button>
                    </span>
                  </label>
                  {signupForm.formState.errors.agreeTerms && (
                    <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.agreeTerms.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={signupLoading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-600 rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-70"
                  style={{ backgroundColor: '#00D4FF', color: '#0B1120' }}
                >
                  {signupLoading ? (
                    <>
                      <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                      Creating your workspace…
                    </>
                  ) : (
                    'Start Free Trial →'
                  )}
                </button>
              </form>
            </div>
          )}

          <p className="text-center text-xs text-gray-400 mt-6">
            {mode === 'login' ? (
              <>
                New to AgentOS?{' '}
                <button onClick={() => setMode('signup')} className="font-500 hover:underline" style={{ color: '#00D4FF' }}>Create a free account</button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="font-500 hover:underline" style={{ color: '#00D4FF' }}>Sign in</button>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-2xl">
            {forgotPasswordSent ? (
              <div className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(0,212,255,0.1)' }}>
                  <Icon name="EnvelopeIcon" size={28} style={{ color: '#00D4FF' }} />
                </div>
                <h3 className="text-lg font-700 text-gray-900 mb-2">Check your email</h3>
                <p className="text-sm text-gray-500 mb-6">
                  We sent a password reset link to <span className="font-600 text-gray-800">{forgotPasswordForm.getValues('email')}</span>. Check your inbox and follow the instructions.
                </p>
                <button
                  onClick={closeForgotPassword}
                  className="w-full py-2.5 text-sm font-600 rounded-xl hover:opacity-90 transition-all"
                  style={{ backgroundColor: '#00D4FF', color: '#0B1120' }}
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-700 text-gray-900">Reset your password</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Enter your email to receive a reset link</p>
                  </div>
                  <button
                    onClick={closeForgotPassword}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400"
                  >
                    <Icon name="XMarkIcon" size={18} />
                  </button>
                </div>

                <form onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-500 text-gray-700 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      {...forgotPasswordForm.register('email', {
                        required: 'Email is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                      })}
                      placeholder="sarah@realty.com"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all placeholder-gray-400 text-gray-900"
                    />
                    {forgotPasswordForm.formState.errors.email && (
                      <p className="text-xs text-red-500 mt-1">{forgotPasswordForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-600 rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-70"
                    style={{ backgroundColor: '#00D4FF', color: '#0B1120' }}
                  >
                    {forgotPasswordLoading ? (
                      <>
                        <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}